import {
  addDays,
  addMonths,
  startOfDay,
  endOfDay,
  format,
  isEqual,
  type Locale,
} from 'date-fns'

const DATE_FORMAT = 'LLL d, Y'

export interface DateRange {
  id: string
  label: string
  startDate: Date | null
  endDate: Date | null
}

export const getDefaultRanges = (date: Date): DateRange[] => [
  {
    id: 'all',
    label: 'all',
    startDate: undefined,
    endDate: undefined,
  },
  {
    id: 'today',
    label: 'today',
    startDate: startOfDay(date),
    endDate: endOfDay(date),
  },
  {
    id: 'yesterday',
    label: 'yesterday',
    startDate: addDays(startOfDay(date), -1),
    endDate: addDays(endOfDay(date), -1),
  },
  {
    id: 'last7Days',
    label: 'last7Days',
    startDate: addDays(startOfDay(date), -7),
    endDate: endOfDay(date),
  },
  {
    id: 'last30Days',
    label: 'last30Days',
    startDate: addDays(startOfDay(date), -30),
    endDate: endOfDay(date),
  },
  {
    id: 'last12Months',
    label: 'last12Months',
    startDate: addMonths(startOfDay(date), -12),
    endDate: endOfDay(date),
  },
]

export const formatDateRangeLabel = (
  startDate: Date,
  endDate: Date,
  locale?: Locale
): string =>
  `${format(startDate, DATE_FORMAT, { locale })} - ${format(endDate, DATE_FORMAT, { locale })}`

export const getCustomRange = (
  range: DateRange,
  locale?: Locale
): DateRange => {
  if (!range) return null

  const { startDate, endDate } = range

  return {
    id: 'custom',
    label: formatDateRangeLabel(startDate, endDate, locale),
    startDate,
    endDate,
  }
}

export const getSelectedDateRange = (
  range: DateRange,
  ranges: DateRange[],
  locale?: Locale
): DateRange => {
  if (!range || (!range.startDate && !range.endDate)) return ranges[0]

  const { startDate, endDate } = range
  const found: DateRange = ranges.find(
    (dr) => isEqual(dr.startDate, startDate) && isEqual(dr.endDate, endDate)
  )

  if (found) return found
  return getCustomRange(range, locale)
}
